export interface GuideLauncherPosition {
  /** Horizontal centre of the launcher, normalized to the current content area. */
  x: number;
  /** Vertical centre of the launcher, normalized to the current content area. */
  y: number;
}

type StoredGuideLauncherPositions = Record<string, GuideLauncherPosition>;

export const DEFAULT_GUIDE_LAUNCHER_POSITION: GuideLauncherPosition = {
  x: 0.94,
  y: 0.9,
};

export const guideLauncherPositionStorageKey = (baseKey: string): string =>
  `${baseKey}:guide-launcher-position:v1`;

const isPosition = (value: unknown): value is GuideLauncherPosition => {
  if (!value || typeof value !== 'object') return false;
  const position = value as Partial<GuideLauncherPosition>;
  return (
    typeof position.x === 'number' &&
    Number.isFinite(position.x) &&
    typeof position.y === 'number' &&
    Number.isFinite(position.y)
  );
};

export const clampGuideLauncherPosition = (
  position: GuideLauncherPosition
): GuideLauncherPosition => ({
  x: Math.min(1, Math.max(0, position.x)),
  y: Math.min(1, Math.max(0, position.y)),
});

export const readGuideLauncherPosition = (
  storageKey: string,
  userLogin: string | null
): GuideLauncherPosition => {
  if (!userLogin) return DEFAULT_GUIDE_LAUNCHER_POSITION;
  try {
    const parsed = JSON.parse(
      localStorage.getItem(storageKey) ?? '{}'
    ) as StoredGuideLauncherPositions;
    return isPosition(parsed[userLogin])
      ? clampGuideLauncherPosition(parsed[userLogin])
      : DEFAULT_GUIDE_LAUNCHER_POSITION;
  } catch {
    return DEFAULT_GUIDE_LAUNCHER_POSITION;
  }
};

export const writeGuideLauncherPosition = (
  storageKey: string,
  userLogin: string | null,
  position: GuideLauncherPosition
): void => {
  if (!userLogin) return;
  let stored: StoredGuideLauncherPositions = {};
  try {
    stored = JSON.parse(
      localStorage.getItem(storageKey) ?? '{}'
    ) as StoredGuideLauncherPositions;
  } catch {
    // Replace invalid local preferences while preserving normal app operation.
  }
  stored[userLogin] = clampGuideLauncherPosition(position);
  try {
    localStorage.setItem(storageKey, JSON.stringify(stored));
  } catch {
    // A blocked/quota-limited storage must never make the guide unusable.
  }
};
